import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import { useEffect } from "react";
import { useEmitirNotaCredito } from "../hooks/useEmitirNotaDeCredito";
import { useAplicacionesFactura } from "../hooks/useAplicacionesFacturas";
import { useAnularFactura } from "../hooks/useAnularFacturas";
import moment from "moment";

import { logEventos } from "../../../../../../../../services/services";
import { useRevertirAplicaciones } from "../hooks/useRevertirAplicaciones";
import { useGetIDsDeAplicaciones } from "../hooks/useGetIDsDeAplicaciones";

export const DesaplicarFacturasModal = ({
  open,
  onClose,
  datos,
  envasado = 0,
  idFactura,
  onSuccess,
  showToast,
}) => {
  // console.log(datos);
  const {
    aplicaciones,
    loading: loadingApps,
    fetchAplicaciones,
    reset,
  } = useAplicacionesFactura();

  const {
    execute: emitirNC,
    loading: loadingNC,
    error: errorNC,
  } = useEmitirNotaCredito();

  const {
    execute: revertirAplicaciones,
    loading: loadingRevertir,
    error: errorRevertir,
  } = useRevertirAplicaciones();

  const {
    execute: getIDsAplicaciones,
    loading: loadingGetIDs,
    error: errorGetIDs,
  } = useGetIDsDeAplicaciones();

  const { execute: anularFactura, loading: loadingAnular } = useAnularFactura();

  const isLoading =
    loadingApps ||
    loadingAnular ||
    loadingNC ||
    loadingGetIDs ||
    loadingRevertir;

  useEffect(() => {
    if (open && idFactura) {
      fetchAplicaciones(idFactura).catch((err) => {
        showToast?.(err.message, "error");
        onClose();
      });
    }

    if (!open) {
      reset();
    }
  }, [open, idFactura]);

  const total = aplicaciones.reduce((acc, a) => acc + a.importe, 0);

  const handleConfirm = async () => {
    const confirm = window.confirm(
      "Se generará una Nota de Crédito y se anulará la factura.\n\n¿Desea continuar?",
    );
    if (!confirm) return;

    const username = localStorage.getItem("user");

    let idsAplicaciones;
    try {
      idsAplicaciones = await getIDsAplicaciones(idFactura);
    } catch (err) {
      showToast?.("Error al obtener las aplicaciones", "error");
      return;
    }

    try {
      const res = await anularFactura(idFactura, username);
      showToast?.(
        `${res.aplicacionesAnuladas} aplicaciones anuladas`,
        "success",
      );
    } catch (err) {
      showToast?.("Fallo al anular las aplicaciones", "error");
      return;
    }

    try {
      await emitirNC(datos, envasado);
      showToast?.("Nota de crédito generada correctamente", "success");
    } catch (err) {
      try {
        await revertirAplicaciones(idsAplicaciones);
        showToast?.(
          `Falló la NC. ${idsAplicaciones.length === 0 ? "" : "Las aplicaciones fueron revertidas"}`,
          "error",
        );
      } catch (revertErr) {
        logEventos(
          "ERROR_REVERTIR_APLICACIONES",
          "FACTURAS",
          `Fallo al revertir aplicaciones de factura ${idFactura}. IDs a revertir manualmente: ${idsAplicaciones.join(", ")}`,
          username,
        );
        showToast?.(
          `Falló la NC y no se pudieron revertir las aplicaciones. IDs a revertir manualmente: ${idsAplicaciones.join(", ")}`,
          "error",
        );
      }
      return;
    }

    logEventos(
      "ANULACION_FACTURA_CON_NC",
      "FACTURAS",
      `Factura ${idFactura} anulada correctamente (Nota de credito generada)`,
      username,
    );

    onSuccess?.();
    onClose();
    return;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: "error.main", color: "white", py: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <WarningIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                ANULAR {datos.origen}
              </Typography>
              <Typography variant="subtitle2">ID: {idFactura}</Typography>
              <Typography variant="subtitle2">
                Total de la factura: ${datos.importe_total}
              </Typography>
            </Box>
          </Box>

          <Chip
            label={`N° ${datos.nro_comprobante}`}
            sx={{
              bgcolor: "white",
              color: "error.main",
              fontWeight: "bold",
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Paper sx={{ p: 3, bgcolor: "warning.50" }} elevation={0}>
          <Box mt={1}>
            <Typography variant="body1">
              <strong> - Se desaplicarán todos los recibos asociados</strong>
            </Typography>
            <Typography variant="body1">
              <strong> - Se generará una Nota de Crédito</strong>
            </Typography>
          </Box>
        </Paper>

        <Divider />

        <Box sx={{ p: 3 }}>
          {loadingApps ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {aplicaciones.length > 0 ? (
                <>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ mb: 2, color: "error.main" }}
                  >
                    APLICACIONES A DESHACER
                  </Typography>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Recibo</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Importe</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {aplicaciones.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>{a.nro_recibo}</TableCell>
                          <TableCell>
                            {moment(a.fecha).format("DD/MM/YYYY")}
                          </TableCell>
                          <TableCell align="right">
                            ${a.importe.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="flex-end">
                    <Typography variant="h6" fontWeight="bold">
                      Total a desaplicar: ${total.toFixed(2)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Typography variant="body1">
                  La factura no tiene aplicaciones asociadas.
                </Typography>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          startIcon={<CloseIcon />}
          disabled={isLoading}
        >
          Cancelar
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? "Procesando..." : "Confirmar anulación"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
